import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from '../contexts/NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import { IAlert } from '../types/entities/IAlert';
import { createSharedSignalRConnection } from '../services/serverNotifications/sharedSignalRConnection';
import { useActivitySession } from './useActivitySession';

/**
 * Connects React consumers to the session's shared notification transport.
 * @param onAlert Receives every live alert.
 * @param onConnected Reconciles data when the shared connection becomes available.
 * @param onResolved Applies confirmed resolutions received from another tab.
 * @returns Connection state and a confirmed-resolution broadcaster.
 */
const useSignalRConnection = (
    onAlert: (alert: IAlert) => void,
    onConnected?: () => void,
    onResolved?: (ids: string[]) => void
) => {
    const session = useActivitySession();
    const { setErrorMessage } = useNotification();
    const callbacksRef = useRef({
        onAlert,
        onConnected,
        onResolved,
        setErrorMessage,
    });
    callbacksRef.current = {
        onAlert,
        onConnected,
        onResolved,
        setErrorMessage,
    };
    const transportRef =
        useRef<ReturnType<typeof createSharedSignalRConnection>>();
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        setIsConnected(false);
        if (!session.isAuthenticated) {
            return;
        }
        const endpoint = process.env.REACT_APP_SIGNAL_R_ENDPOINT?.trim();
        if (!endpoint) {
            callbacksRef.current.setErrorMessage(
                FeedbackMessages.SIGNALR_CONFIGURATION_ERROR
            );
            return;
        }
        let connected = false;
        const transport = createSharedSignalRConnection({
            endpoint,
            ...session,
            onAlert: alert => callbacksRef.current.onAlert(alert),
            onResolved: ids => callbacksRef.current.onResolved?.(ids),
            onError: message => callbacksRef.current.setErrorMessage(message),
            onStatus: nextConnected => {
                setIsConnected(nextConnected);
                if (nextConnected && !connected) {
                    callbacksRef.current.onConnected?.();
                }
                connected = nextConnected;
            },
        });
        transportRef.current = transport;
        return () => {
            transportRef.current = undefined;
            transport.dispose();
        };
    }, [session]);

    const broadcastResolved = useCallback((ids: string[]) => {
        transportRef.current?.broadcastResolved(ids);
    }, []);

    return {
        isConnected: session.isAuthenticated && isConnected,
        broadcastResolved,
    };
};

export default useSignalRConnection;
